import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, InputGroup, Pagination, Modal } from 'react-bootstrap';
import axios from 'axios';
import DeliveryTimeSelector from '../components/DeliveryTimeSelector';

const ManagerOrders = () => {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  // Orders state
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [orderFilters, setOrderFilters] = useState({ tableNumber: '', status: '' });
  
  // Enhanced order display state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(12);
  const [viewMode, setViewMode] = useState('cards');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showDeliveryTimeModal, setShowDeliveryTimeModal] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState('');

  // Menus for order filtering
  const [menus, setMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [menusError, setMenusError] = useState('');
  const [selectedOrdersMenuId, setSelectedOrdersMenuId] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      fetchMenus();
      fetchOrders();
    }
  }, [profile]);

  // WebSocket handles real-time updates, no need for manual refresh on menu change

  // Real-time order updates using WebSocket
  useEffect(() => {
    if (!profile) return;
    
    let fallbackInterval = null;
    let ws = null;
    let isConnecting = false;
    let isMounted = true;
    
    const connectWebSocket = () => {
      if (!isMounted || isConnecting || (ws && ws.readyState === WebSocket.OPEN)) {
        return; // Already connecting or connected, or component unmounted
      }
      
      isConnecting = true;
      const restaurantId = profile.restaurantId || profile._id;
      const wsUrl = `wss://menu.qr-code.by/ws/orders/${restaurantId}`;
      
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        if (!isMounted) {
          ws.close();
          return;
        }
        isConnecting = false;
        // Clear any existing fallback interval
        if (fallbackInterval) {
          clearInterval(fallbackInterval);
          fallbackInterval = null;
        }
      };
      
      ws.onmessage = (event) => {
        if (!isMounted) return;
        
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'new_order' || data.type === 'order_updated') {
            // Refresh orders when new order arrives
            fetchOrders();
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        if (!isMounted) return;
        
        isConnecting = false;
        // Start fallback polling if not already running
        if (!fallbackInterval) {
          fallbackInterval = setInterval(() => {
            if (isMounted) {
              fetchOrders();
            }
          }, 10000); // Poll every 10 seconds as fallback
        }
      };
      
      ws.onclose = (event) => {
        if (!isMounted) return;
        
        isConnecting = false;
        
        // Only start fallback if it's not a normal closure and component is still mounted
        if (event.code !== 1000 && !fallbackInterval && isMounted) {
          fallbackInterval = setInterval(() => {
            if (isMounted) {
              fetchOrders();
            }
          }, 10000); // Poll every 10 seconds as fallback
        }
      };
    };
    
    // Connect immediately
    connectWebSocket();

    return () => {
      isMounted = false;
      if (ws) {
        ws.close(1000, 'Component unmounting');
      }
      if (fallbackInterval) {
        clearInterval(fallbackInterval);
      }
    };
  }, [profile?.restaurantId, profile?._id]); // Only depend on the actual IDs, not the whole profile object

  useEffect(() => {
    let filtered = orders;
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }
    
    // Filter by date
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.createdAt);
        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'yesterday':
            return orderDate >= yesterday && orderDate < today;
          case 'week':
            return orderDate >= weekAgo;
          default:
            return true;
        }
      });
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.tableNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [orders, statusFilter, dateFilter, searchTerm]);

  const fetchProfile = async () => {
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      if (!token) {
        setProfileError('Токен не найден');
        setLoadingProfile(false);
        return;
      }

      // Determine which API endpoint to use based on token type
      const profileUrl = restaurantToken ? '/api/auth/profile' : '/api/manager/profile';
      
      const res = await axios.get(profileUrl, {
        headers: { 'x-auth-token': token }
      });
      setProfile(res.data);
    } catch (err) {
      setProfileError(err.response?.data?.message || 'Ошибка загрузки профиля');
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchMenus = async () => {
    try {
      setLoadingMenus(true);
      setMenusError('');
      
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      if (!token) {
        setMenusError('Токен не найден');
        setLoadingMenus(false);
        return;
      }

      let restaurantId;
      
      if (restaurantToken) {
        // For restaurant owners, use the profile ID directly
        restaurantId = profile._id;
      } else {
        // For managers, get restaurantId from profile
        const profileRes = await axios.get('/api/manager/profile', {
          headers: { 'x-auth-token': token }
        });
        restaurantId = profileRes.data.restaurantId;
      }
      
      const res = await axios.get(`/api/menu/${restaurantId}/list`, {
        headers: { 'x-auth-token': token }
      });
      setMenus(res.data);
    } catch (err) {
      setMenusError(err.response?.data?.message || 'Ошибка загрузки меню');
    } finally {
      setLoadingMenus(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      setOrdersError('');

      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      if (!token) {
        setOrdersError('Токен не найден');
        setLoadingOrders(false);
        return;
      }

      const params = new URLSearchParams();
      
      if (selectedOrdersMenuId) {
        params.set('menuId', selectedOrdersMenuId);
      }

      // Determine which API endpoint to use based on token type
      const ordersUrl = restaurantToken 
        ? `/api/orders/restaurant/${profile._id}?${params.toString()}`
        : `/api/manager/orders?${params.toString()}`;

      const res = await axios.get(ordersUrl, {
        headers: { 'x-auth-token': token }
      });
      
      // Only update if data has changed
      const newOrders = res.data;
      setOrders(prevOrders => {
        // Compare if orders have actually changed
        if (JSON.stringify(prevOrders) === JSON.stringify(newOrders)) {
          return prevOrders; // No change, keep previous reference
        }
        return newOrders;
      });
    } catch (err) {
      setOrdersError(err.response?.data?.message || 'Ошибка загрузки заказов');
    } finally {
      setLoadingOrders(false);
    }
  };

  const getMenuLabel = (id) => {
    const m = menus.find((x) => x._id === id);
    if (!m) return '—';
    return `${m.title} — ${m.mode === 'dine_in' ? 'В зале' : 'Доставка'}`;
  };

  // Order status badge helper
  const getOrderStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Ожидает</Badge>;
      case 'accepted':
        return <Badge bg="info">Принят</Badge>;
      case 'completed':
        return <Badge bg="success">Завершен</Badge>;
      case 'rejected':
        return <Badge bg="danger">Отклонен</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Payment status badge helper
  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Ожидает оплаты</Badge>;
      case 'paid':
        return <Badge bg="success">Оплачен</Badge>;
      case 'completed':
        return <Badge bg="success">Оплачен</Badge>;
      case 'failed':
        return <Badge bg="danger">Ошибка оплаты</Badge>;
      case 'refunded':
        return <Badge bg="info">Возвращен</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Status icon helper
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'accepted':
        return '✅';
      case 'completed':
        return '🎉';
      case 'rejected':
        return '❌';
      default:
        return '❓';
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      // Find the order to check order type
      const order = orders.find(o => o._id === orderId);
      const isDelivery = order?.orderType === 'delivery';
      
      // If accepting delivery order, show delivery time selector
      if (status === 'accepted' && isDelivery) {
        setPendingOrderId(orderId);
        setPendingStatus(status);
        setShowDeliveryTimeModal(true);
        return;
      }
      
      // Use appropriate API endpoint based on token type
      const apiUrl = restaurantToken 
        ? `/api/orders/${orderId}/status`
        : `/api/manager/orders/${orderId}/status`;
        
      const response = await axios.put(
        apiUrl,
        { status },
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update the order in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order._id === orderId 
              ? { ...order, status: status, processedBy: response.data.order?.processedBy }
              : order
          )
        );
        
        // Update selected order if it's the same
        if (selectedOrder && selectedOrder._id === orderId) {
          setSelectedOrder(prev => {
            if (!prev) return prev; // Guard against null
            return { ...prev, status: status, processedBy: response.data.order?.processedBy };
          });
        }
        
        // WebSocket will handle real-time updates, no need for manual refresh
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Ошибка при обновлении статуса заказа');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  // Handle delivery time selection
  const handleDeliveryTimeConfirm = async (deliveryTime) => {
    try {
      const managerToken = localStorage.getItem('managerToken');
      const restaurantToken = localStorage.getItem('restaurantToken');
      const token = managerToken || restaurantToken;
      
      const requestBody = { 
        status: pendingStatus,
        deliveryTime: deliveryTime
      };
      
      // Use appropriate API endpoint based on token type
      const apiUrl = restaurantToken 
        ? `/api/orders/${pendingOrderId}/status`
        : `/api/manager/orders/${pendingOrderId}/status`;
      
      const response = await axios.put(
        apiUrl,
        requestBody,
        {
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        // Update the order in the local state
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order._id === pendingOrderId 
              ? { 
                  ...order, 
                  status: pendingStatus,
                  deliveryTime: deliveryTime,
                  processedBy: response.data.order?.processedBy 
                }
              : order
          )
        );
        
        // Update selected order if it's the same
        if (selectedOrder && selectedOrder._id === pendingOrderId) {
          setSelectedOrder(prev => {
            if (!prev) return prev;
            return { 
              ...prev, 
              status: pendingStatus,
              deliveryTime: deliveryTime,
              processedBy: response.data.order?.processedBy 
            };
          });
        }
      }
    } catch (error) {
      console.error('Error updating order with delivery time:', error);
      alert('Ошибка при обновлении заказа с временем доставки');
    } finally {
      setShowDeliveryTimeModal(false);
      setPendingOrderId(null);
      setPendingStatus('');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'BYN'
    }).format(price);
  };

  // Pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  if (loadingProfile) {
    return <div className="mt-5 text-center">Загрузка профиля менеджера...</div>;
  }

  if (profileError) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Ошибка загрузки профиля</Alert.Heading>
          <p>{profileError}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-5">
      <Row className="mb-4">
        <Col>
          <h1>Управление заказами</h1>
          <p className="mb-0">
            Ресторан: <strong>{profile?.restaurantName || '—'}</strong>
          </p>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              <Card.Title>Заказы клиентов</Card.Title>
              <p>Просматривайте и управляйте заказами клиентов.</p>

              {/* Панель фильтров и поиска */}
              <div className="no-print mb-3">
                <Row className="g-2">
                  <Col md={4}>
                    <InputGroup size="sm">
                      <InputGroup.Text>Поиск</InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="По номеру заказа, столу или клиенту..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={2}>
                    <Form.Select
                      size="sm"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">Все статусы</option>
                      <option value="pending">Ожидает</option>
                      <option value="accepted">Принят</option>
                      <option value="completed">Завершен</option>
                      <option value="rejected">Отклонен</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Select
                      size="sm"
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                    >
                      <option value="all">Все даты</option>
                      <option value="today">Сегодня</option>
                      <option value="yesterday">Вчера</option>
                      <option value="week">За неделю</option>
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <Form.Select
                      size="sm"
                      value={selectedOrdersMenuId}
                      onChange={(e) => setSelectedOrdersMenuId(e.target.value)}
                    >
                      <option value="">Все меню</option>
                      {menus.map((menu) => (
                        <option key={menu._id} value={menu._id}>
                          {menu.title}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={2}>
                    <div className="d-flex gap-1">
                      <Button
                        variant={viewMode === 'cards' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setViewMode('cards')}
                      >
                        📋 Карточки
                      </Button>
                      <Button
                        variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                        size="sm"
                        onClick={() => setViewMode('table')}
                      >
                        📊 Таблица
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>

              {ordersError && <Alert variant="danger">{ordersError}</Alert>}

              {loadingOrders ? (
                <div className="text-center py-3">
                  <div className="text-muted">Загрузка заказов...</div>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-3">
                  <h6 className="text-muted">Заказы не найдены</h6>
                  <p className="text-muted small">
                    {searchTerm || statusFilter !== 'all' || dateFilter !== 'all' || selectedOrdersMenuId
                      ? 'Попробуйте изменить фильтры поиска'
                      : 'Заказы появятся здесь после их создания клиентами'
                    }
                  </p>
                </div>
              ) : (
                <>
                  {viewMode === 'cards' ? (
                    <Row className="g-3">
                      {currentOrders.map((order) => (
                        <Col key={order._id} xs={12} md={6} lg={4}>
                          <Card 
                            className="h-100 order-card" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOrderClick(order)}
                          >
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <h6 className="mb-0">{order.orderNumber || `#${order._id.slice(-6)}`}</h6>
                                <div className="d-flex gap-1">
                                  {getOrderStatusBadge(order.status)}
                                  {getPaymentStatusBadge(order.paymentStatus)}
                                </div>
                              </div>
                              
                              <div className="mb-2">
                                <small className="text-muted">
                                  {getStatusIcon(order.status)} {formatDate(order.createdAt)}
                                </small>
                              </div>
                              
                              <div className="mb-2">
                                <strong>Меню:</strong> {getMenuLabel(order.menuId)}
                              </div>
                              <div className="mb-2">
                                <strong>Стол:</strong> {order.tableNumber || '—'}
                              </div>
                              
                              <div className="mb-2">
                                <strong>Клиент:</strong> {order.customerName || '—'}
                              </div>
                              
                              <div className="mb-2">
                                <strong>Телефон:</strong> {order.customerPhone || '—'}
                              </div>
                              
                              {/* Информация о времени для всех заказов */}
                              <div className="mb-2">
                                <small className="text-muted">
                                  Создан: {new Date(order.createdAt).toLocaleString('ru-RU')}
                                </small>
                                {order.updatedAt && order.updatedAt !== order.createdAt && (
                                  <>
                                    <br />
                                    <small className="text-muted">
                                      Обновлен: {new Date(order.updatedAt).toLocaleString('ru-RU')}
                                    </small>
                                  </>
                                )}
                              </div>
                              
                              {/* Информация о менеджере для главного менеджера */}
                              {profile?.canManageMenu && order.processedBy && (
                                <div className="mb-2">
                                  <small className="text-muted">
                                    Обработал: <span className="text-primary fw-bold">
                                      {order.processedBy.name || `Менеджер ${order.processedBy.phone}`}
                                    </span>
                                  </small>
                                </div>
                              )}
                              
                              <div className="mb-2">
                                <strong>Сумма:</strong> {formatPrice(order.finalAmount || order.totalAmount)}
                                {order.totalDiscount > 0 && (
                                  <div className="small text-success">
                                    <span className="text-muted">Скидка:</span> -{formatPrice(order.totalDiscount)}
                                    {order.subtotalAmount && order.subtotalAmount !== order.finalAmount && (
                                      <span className="text-muted"> (было: {formatPrice(order.subtotalAmount)})</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              <div className="mb-3">
                                <strong>Блюда:</strong>
                                <div className="mt-1">
                                  {order.items?.slice(0, 3).map((item, index) => {
                                    let itemText = `${item.quantity}x ${item.name}`;
                                    
                                    // Add addons info
                                    if (item.addons && item.addons.length > 0) {
                                      const addonNames = item.addons.map(addon => addon.name).join(', ');
                                      itemText += ` (+${addonNames})`;
                                    }
                                    
                                    // Add addonGroups info
                                    if (item.addonGroups && item.addonGroups.length > 0) {
                                      const groupOptions = item.addonGroups.map(group => 
                                        group.options?.map(option => option.name).join(', ')
                                      ).filter(Boolean).join(', ');
                                      if (groupOptions) {
                                        itemText += ` (+${groupOptions})`;
                                      }
                                    }
                                    
                                    return (
                                      <div key={index} className="small text-muted">
                                        {itemText}
                                      </div>
                                    );
                                  })}
                                  {order.items?.length > 3 && (
                                    <div className="small text-muted">
                                      ... и еще {order.items.length - 3} блюд
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Кнопки управления заказом */}
                              {order.status === 'pending' && (profile?.canManageMenu || localStorage.getItem('restaurantToken')) && (
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="success" 
                                    size="sm" 
                                    className="flex-fill"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateOrderStatus(order._id, 'accepted');
                                    }}
                                  >
                                    ✅ Принять
                                  </Button>
                                  <Button 
                                    variant="danger" 
                                    size="sm" 
                                    className="flex-fill"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateOrderStatus(order._id, 'rejected');
                                    }}
                                  >
                                    ❌ Отклонить
                                  </Button>
                                </div>
                              )}
                              
                              {order.status === 'accepted' && (
                                <div className="d-flex gap-2">
                                  <Button 
                                    variant="primary" 
                                    size="sm" 
                                    className="flex-fill"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updateOrderStatus(order._id, 'completed');
                                    }}
                                  >
                                    ✅ Завершить
                                  </Button>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <Table striped hover responsive>
                      <thead>
                        <tr>
                          <th>№</th>
                          <th>Статус</th>
                          <th>Оплата</th>
                          <th>Стол</th>
                          <th>Клиент</th>
                          <th>Меню</th>
                          <th>Сумма</th>
                          <th>Дата</th>
                          <th>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.map((order) => (
                          <tr key={order._id}>
                            <td>{order.orderNumber || `#${order._id.slice(-6)}`}</td>
                            <td>{getOrderStatusBadge(order.status)}</td>
                            <td>{getPaymentStatusBadge(order.paymentStatus)}</td>
                            <td>{order.tableNumber || '—'}</td>
                            <td>
                              <div>{order.customerName || '—'}</div>
                              <small className="text-muted">{order.customerPhone || '—'}</small>
                            </td>
                            <td>{getMenuLabel(order.menuId)}</td>
                            <td>
                              {formatPrice(order.finalAmount || order.totalAmount)}
                              {order.totalDiscount > 0 && (
                                <div className="small text-success">
                                  -{formatPrice(order.totalDiscount)}
                                </div>
                              )}
                            </td>
                            <td>
                              <div>{formatDate(order.createdAt)}</div>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleOrderClick(order)}
                                >
                                  👁️ Просмотр
                                </Button>
                                
                                {order.status === 'pending' && (profile?.canManageMenu || localStorage.getItem('restaurantToken')) && (
                                  <>
                                    <Button
                                      variant="success"
                                      size="sm"
                                      onClick={() => updateOrderStatus(order._id, 'accepted')}
                                    >
                                      ✅ Принять
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      onClick={() => updateOrderStatus(order._id, 'rejected')}
                                    >
                                      ❌ Отклонить
                                    </Button>
                                  </>
                                )}
                                
                                {order.status === 'accepted' && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => updateOrderStatus(order._id, 'completed')}
                                  >
                                    ✅ Завершить
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                      <Pagination>
                        <Pagination.Prev
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        />
                        {[...Array(totalPages)].map((_, index) => (
                          <Pagination.Item
                            key={index + 1}
                            active={currentPage === index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Order Details Modal */}
      <Modal show={showOrderModal && selectedOrder} onHide={handleCloseOrderModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Заказ {selectedOrder?.orderNumber || `#${selectedOrder?._id.slice(-6)}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>Информация о заказе</h6>
                  <p><strong>Статус:</strong> {getOrderStatusBadge(selectedOrder.status)}</p>
                  <p><strong>Оплата:</strong> {getPaymentStatusBadge(selectedOrder.paymentStatus)}</p>
                  <p><strong>Стол:</strong> {selectedOrder.tableNumber || '—'}</p>
                  <p><strong>Дата создания:</strong> {new Date(selectedOrder.createdAt).toLocaleString('ru-RU')}</p>
                  {selectedOrder.updatedAt && selectedOrder.updatedAt !== selectedOrder.createdAt && (
                    <p><strong>Дата обновления:</strong> {new Date(selectedOrder.updatedAt).toLocaleString('ru-RU')}</p>
                  )}
                  {profile?.canManageMenu && selectedOrder.processedBy && (
                    <p><strong>Обработал:</strong> <span className="text-primary fw-bold">
                      {selectedOrder.processedBy.name || `Менеджер ${selectedOrder.processedBy.phone}`}
                    </span></p>
                  )}
                </Col>
                <Col md={6}>
                  <h6>Информация о клиенте</h6>
                  <p><strong>Имя:</strong> {selectedOrder.customerName || '—'}</p>
                  <p><strong>Телефон:</strong> {selectedOrder.customerPhone || '—'}</p>
                  {selectedOrder.orderType === 'delivery' && selectedOrder.deliveryAddress && (
                    <>
                      <p><strong>Адрес доставки:</strong> {selectedOrder.deliveryAddress}</p>
                      {selectedOrder.deliveryEntrance && (
                        <p><strong>Подъезд:</strong> {selectedOrder.deliveryEntrance}</p>
                      )}
                      {selectedOrder.deliveryFloor && (
                        <p><strong>Этаж:</strong> {selectedOrder.deliveryFloor}</p>
                      )}
                      {selectedOrder.deliveryTime && (
                        <div className="mb-2">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-clock text-primary me-2"></i>
                            <strong>Время доставки:</strong>
                          </div>
                          <div 
                            className="mt-1 p-2 rounded" 
                            style={{ 
                              backgroundColor: '#e3f2fd', 
                              border: '1px solid #2196f3',
                              borderRadius: '6px',
                              display: 'inline-block'
                            }}
                          >
                            <span className="text-primary fw-bold">{selectedOrder.deliveryTime}</span>
                          </div>
                        </div>
                      )}
                      {selectedOrder.deliveryTimeApproved !== undefined && (
                        <div className="mb-2">
                          <div className="d-flex align-items-center mb-1">
                            <i className="fas fa-handshake text-info me-2"></i>
                            <strong>Согласие на время:</strong>
                          </div>
                          {selectedOrder.deliveryTimeApproved === true ? (
                            <Badge 
                              bg="success" 
                              className="d-inline-flex align-items-center"
                              style={{ 
                                fontSize: '0.9rem', 
                                padding: '8px 16px',
                                borderRadius: '15px',
                                fontWeight: '600'
                              }}
                            >
                              <i className="fas fa-check-circle me-2"></i>
                              Согласен
                            </Badge>
                          ) : selectedOrder.deliveryTimeApproved === false ? (
                            <Badge 
                              bg="danger" 
                              className="d-inline-flex align-items-center"
                              style={{ 
                                fontSize: '0.9rem', 
                                padding: '8px 16px',
                                borderRadius: '15px',
                                fontWeight: '600'
                              }}
                            >
                              <i className="fas fa-times-circle me-2"></i>
                              Отказался
                            </Badge>
                          ) : (
                            <Badge 
                              bg="warning" 
                              className="d-inline-flex align-items-center"
                              style={{ 
                                fontSize: '0.9rem', 
                                padding: '8px 16px',
                                borderRadius: '15px',
                                fontWeight: '600'
                              }}
                            >
                              <i className="fas fa-hourglass-half me-2"></i>
                              Ожидает ответа
                            </Badge>
                          )}
                        </div>
                      )}
                      {selectedOrder.callBack && (
                        <div className="mb-2">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-phone text-success me-2"></i>
                            <strong>Клиент просит перезвонить</strong>
                            <Badge 
                              bg="info" 
                              className="ms-2 d-inline-flex align-items-center"
                              style={{ 
                                fontSize: '0.8rem', 
                                padding: '6px 12px',
                                borderRadius: '12px',
                                fontWeight: '600'
                              }}
                            >
                              <i className="fas fa-phone me-1"></i>
                              Перезвонить
                            </Badge>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <p><strong>Меню:</strong> {getMenuLabel(selectedOrder.menuId)}</p>
                </Col>
              </Row>
              
              <h6>Блюда в заказе</h6>
              <Table striped>
                <thead>
                  <tr>
                    <th>Блюдо</th>
                    <th>Количество</th>
                    <th>Цена</th>
                    <th>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, index) => {
                    // Calculate total price including addons
                    let itemTotalPrice = item.price;
                    
                    // Add addons price
                    if (item.addons && item.addons.length > 0) {
                      itemTotalPrice += item.addons.reduce((sum, addon) => sum + (addon.price || 0), 0);
                    }
                    
                    // Add addonGroups price
                    if (item.addonGroups && item.addonGroups.length > 0) {
                      item.addonGroups.forEach(group => {
                        if (group.options && group.options.length > 0) {
                          itemTotalPrice += group.options.reduce((sum, option) => sum + (option.price || 0), 0);
                        }
                      });
                    }
                    
                    return (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            <div>{item.name}</div>
                            {item.description && (
                              <small className="text-muted">{item.description}</small>
                            )}
                            {/* Display addons */}
                            {item.addons && item.addons.length > 0 && (
                              <div className="mt-1">
                                {item.addons.map((addon, addonIndex) => (
                                  <div key={addonIndex} className="small text-success">
                                    + {addon.name} {addon.price > 0 && `(+${formatPrice(addon.price)})`}
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Display addonGroups */}
                            {item.addonGroups && item.addonGroups.length > 0 && (
                              <div className="mt-1">
                                {item.addonGroups.map((group, groupIndex) => (
                                  <div key={groupIndex}>
                                    <div className="small text-muted">{group.name}:</div>
                                    {group.options && group.options.map((option, optionIndex) => (
                                      <div key={optionIndex} className="small text-success ms-2">
                                        + {option.name} {option.price > 0 && `(+${formatPrice(option.price)})`}
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td>{item.quantity}</td>
                          <td>{formatPrice(itemTotalPrice)}</td>
                          <td>{formatPrice(itemTotalPrice * item.quantity)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan="3">Итого:</th>
                    <th>{formatPrice(selectedOrder.finalAmount || selectedOrder.totalAmount)}</th>
                  </tr>
                  {selectedOrder.totalDiscount > 0 && (
                    <tr>
                      <th colSpan="3" className="text-success">Скидка:</th>
                      <th className="text-success">-{formatPrice(selectedOrder.totalDiscount)}</th>
                    </tr>
                  )}
                  {selectedOrder.subtotalAmount && selectedOrder.subtotalAmount !== selectedOrder.finalAmount && (
                    <tr>
                      <th colSpan="3" className="text-muted">Было:</th>
                      <th className="text-muted">{formatPrice(selectedOrder.subtotalAmount)}</th>
                    </tr>
                  )}
                </tfoot>
              </Table>
              
              {selectedOrder.appliedPromotions && selectedOrder.appliedPromotions.length > 0 && (
                <div className="mt-3">
                  <h6>Применённые акции</h6>
                  {selectedOrder.appliedPromotions.map((promotion, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
                      <div>
                        <div style={{ fontWeight: '600', color: promotion.color || '#ff6b6b' }}>
                          {promotion.title}
                        </div>
                        {promotion.description && (
                          <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                            {promotion.description}
                          </div>
                        )}
                        <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                          {promotion.message}
                        </div>
                      </div>
                      <span style={{ color: '#28a745', fontWeight: 'bold' }}>
                        -{formatPrice(promotion.discount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {selectedOrder.notes && (
                <div className="mt-3">
                  <h6>Примечания</h6>
                  <p className="text-muted">{selectedOrder.notes}</p>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex gap-2 w-100">
            <Button variant="secondary" onClick={handleCloseOrderModal}>
              Закрыть
            </Button>
            
            {selectedOrder?.status === 'pending' && (profile?.canManageMenu || localStorage.getItem('restaurantToken')) && (
              <>
                <Button 
                  variant="success" 
                  onClick={() => {
                    updateOrderStatus(selectedOrder._id, 'accepted');
                    handleCloseOrderModal();
                  }}
                >
                  ✅ Принять заказ
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => {
                    updateOrderStatus(selectedOrder._id, 'rejected');
                    handleCloseOrderModal();
                  }}
                >
                  ❌ Отклонить заказ
                </Button>
              </>
            )}
            
            {selectedOrder?.status === 'accepted' && (
              <Button 
                variant="primary" 
                onClick={() => {
                  updateOrderStatus(selectedOrder._id, 'completed');
                  handleCloseOrderModal();
                }}
              >
                ✅ Завершить заказ
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Delivery Time Selector Modal */}
      <DeliveryTimeSelector
        show={showDeliveryTimeModal}
        onHide={() => {
          setShowDeliveryTimeModal(false);
          setPendingOrderId(null);
          setPendingStatus('');
        }}
        onConfirm={handleDeliveryTimeConfirm}
        orderType="delivery"
      />
    </Container>
  );
};

export default ManagerOrders;
